import { useMemo, useState } from "react";
import {
  disableFoodRetailSource,
  enableFoodRetailSource,
  fetchFoodRetailIngestionReadiness,
  fetchFoodRetailSourceEnablementStatus,
  runFoodRetailIngestion,
} from "../../../../api/foodApi";
import { ADMIN_HASH_BY_VIEW } from "../../../../auth/adminAccess";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { useAuth } from "../../../../auth/useAuth";
import { foodSourceGroupLabel } from "../../../../constants/foodSourceConstants";
import FoodSourceControlsGrid from "../dataSources/FoodSourceControlsGrid";
import {
  getRunResultMessage,
  parseRetailIngestionApiError,
  RETAIL_INGESTION_LIMITS,
} from "./foodRetailIngestionHelpers";

const RETAIL_INGESTION_HREF = ADMIN_HASH_BY_VIEW["food-retail-ingestion"];

function normalizeHealth(source) {
  if (!source.isActive) {
    return "disabled";
  }

  const status = String(source.healthStatus || "").toLowerCase();
  if (status === "running") {
    return "running";
  }
  if (status === "failing" || status === "failed") {
    return "failed";
  }
  if (status === "stale" || status === "warning") {
    return "warning";
  }
  if (status === "healthy") {
    return "healthy";
  }
  if (status === "discovery_pending" || status === "not_configured") {
    return "never_run";
  }

  return status || "never_run";
}

const FoodRetailSourceControlsTable = ({
  sources,
  filters,
  onRefresh,
  showToast,
  onSourcePatch,
  hideEmpty = false,
}) => {
  const [busyCode, setBusyCode] = useState(null);
  const [runningCode, setRunningCode] = useState(null);
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManageSources =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const filteredSources = useMemo(() => {
    let list = [...(sources || [])];

    if (filters.search) {
      const query = filters.search.toLowerCase();
      list = list.filter(
        (source) =>
          source.sourceName?.toLowerCase().includes(query) ||
          source.sourceCode?.toLowerCase().includes(query) ||
          foodSourceGroupLabel(source.sourceGroup)?.toLowerCase().includes(query)
      );
    }

    if (filters.enabledFilter === "enabled") {
      list = list.filter((source) => source.isActive);
    } else if (filters.enabledFilter === "disabled") {
      list = list.filter((source) => !source.isActive);
    }

    if (filters.statusFilter && filters.statusFilter !== "all") {
      list = list.filter((source) => normalizeHealth(source) === filters.statusFilter);
    }

    return list.sort((a, b) => a.sourceName.localeCompare(b.sourceName));
  }, [sources, filters]);

  if (hideEmpty && !filteredSources.length) {
    return null;
  }

  const patchSource = (sourceCode, patch) => {
    onSourcePatch?.(sourceCode, patch);
  };

  const handleToggleActive = async (source) => {
    if (!canManageSources) {
      return;
    }

    const nextActive = !source.isActive;
    setBusyCode(source.sourceCode);

    try {
      if (nextActive) {
        const enablement = await fetchFoodRetailSourceEnablementStatus(source.sourceCode);
        if (!enablement.canEnable) {
          showToast(
            enablement.blockingReasons?.[0] ||
              `${source.sourceName} cannot be enabled yet.`,
            "error"
          );
          return;
        }

        const confirmed = window.confirm(
          `Enable ${source.sourceName} for manual retail collection? This does not start scheduled ingestion.`
        );
        if (!confirmed) {
          return;
        }

        const result = await enableFoodRetailSource(source.sourceCode, {
          confirm: true,
          enabledBy: "admin",
          notes: "Enabled from Source Controls.",
        });
        patchSource(source.sourceCode, {
          isActive: true,
          healthStatus: "healthy",
          activationNote: null,
        });
        showToast(result.message || `${source.sourceName} enabled.`, "success");
      } else {
        const reason = window.prompt(
          `Reason for disabling ${source.sourceName}:`,
          "Disabled from Source Controls."
        );
        if (!reason?.trim()) {
          return;
        }

        const result = await disableFoodRetailSource(source.sourceCode, {
          confirm: true,
          disabledBy: "admin",
          reason: reason.trim(),
        });
        patchSource(source.sourceCode, {
          isActive: false,
          healthStatus: "disabled",
        });
        showToast(result.message || `${source.sourceName} disabled.`, "success");
      }

      onRefresh?.({ silent: true });
    } catch (error) {
      showToast(parseRetailIngestionApiError(error), "error");
    } finally {
      setBusyCode(null);
    }
  };

  const handleRunNow = async (source) => {
    if (!canManageSources) {
      return;
    }

    setBusyCode(source.sourceCode);
    setRunningCode(source.sourceCode);

    try {
      const readiness = await fetchFoodRetailIngestionReadiness(source.sourceCode);
      const dryRun = !source.isActive;

      if (dryRun && readiness.dryRunAllowed === false) {
        showToast(
          readiness.blockingReasons?.[0] || `${source.sourceName} is not runnable yet.`,
          "error"
        );
        return;
      }

      if (!dryRun) {
        if (readiness.realRunAllowed === false) {
          showToast(
            readiness.blockingReasons?.[0] || `${source.sourceName} cannot run real ingestion yet.`,
            "error"
          );
          return;
        }

        const confirmed = window.confirm(
          `Run real retail ingestion for ${source.sourceName}? This will insert price observations.`
        );
        if (!confirmed) {
          return;
        }
      }

      const result = await runFoodRetailIngestion({
        sourceKey: source.sourceCode,
        dryRun,
        useMock: false,
        maxCategories: RETAIL_INGESTION_LIMITS.DEFAULT_MAX_CATEGORIES,
        maxProductsPerCategory: RETAIL_INGESTION_LIMITS.DEFAULT_MAX_PRODUCTS_PER_CATEGORY,
        categoryUrls: [],
      });

      showToast(getRunResultMessage(result) || `Run completed for ${source.sourceName}.`, "success");
      onRefresh?.({ silent: true });
    } catch (error) {
      showToast(parseRetailIngestionApiError(error), "error");
    } finally {
      setBusyCode(null);
      setRunningCode(null);
    }
  };

  const handleOpenIngestion = (source) => {
    window.location.hash = RETAIL_INGESTION_HREF;
    showToast(`Open Food Retail Ingestion to configure ${source.sourceName}.`, "neutral");
  };

  return (
    <FoodSourceControlsGrid
      sources={filteredSources}
      emptyMessage={
        sources?.length
          ? "No retail supermarket sources match the current filters."
          : "No retail supermarket sources are registered yet."
      }
      canManageSources={canManageSources}
      busyCode={busyCode}
      runningCode={runningCode}
      onToggleEnabled={handleToggleActive}
      onRunNow={handleRunNow}
      onViewRuns={handleOpenIngestion}
      resolveRunNowTitle={(source) =>
        !source.isActive
          ? source.isCollecting
            ? "Run a dry-run validation (enable source for real collection)"
            : "Enable source or complete discovery before running"
          : runningCode === source.sourceCode
            ? "Ingestion run in progress"
            : "Run retail ingestion now"
      }
      onRefresh={onRefresh}
      showToast={showToast}
      onSourcePatch={onSourcePatch}
    />
  );
};

export default FoodRetailSourceControlsTable;
